import { FC, useState, useEffect, useMemo, useCallback, useRef } from 'react'

import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'

import HeaderToolbar from '../Toolbar'
import CustomPopup from '../../UI/Popup'
import CustomButton from '../../UI/Buttons/CustomButton'
import DayCellHover from './DayCellContent'
import CheckboxInput from '../../UI/Inputs/Checkbox'

import './SchedulePostModal.scss'
import NumberInput from '../../UI/Inputs/NumberInput'
import RadioInput from '../../UI/Inputs/Radio/Simple'
import { useDispatch, useSelector } from '../../../store'
import { push } from 'connected-react-router'
import { closeSchedulePostModal } from '../../../store/application/slice'
import {
  getAvailableFansOnlineMetricsBySocialNetwork,
  updateScheduleBeforeApprovalThunk,
  updateSchedulesThunk,
  updateScheduleThunk
} from '../../../store/managePost/thunks'
import {
  setDateAction,
  setTimeAction,
  toggleIsAutopostingAction,
  setIsSchedulingPostAction,
  setIsAutopostingAction
} from '../../../store/managePost/slice'

import BarChart from './BestTimeChart/BarChart'
import { POST_STATUS, POST_TYPE, SOCIAL_NETWORK } from '../../../models/post'
import {
  TPageFansPayload,
  TPostSocialManagePost,
  TUpdatePostSchedulesPayload
} from '../../../store/managePost/newTypes'
import { format, parseISO, isBefore, addMinutes } from 'date-fns'
import { convertToTimeZone } from 'date-fns-timezone'
import { matchPath, useParams } from 'react-router'
import { AUTH_ROUTE } from '../../../pages/App/routes'
import { isFulfilled, isRejected } from '@reduxjs/toolkit'
import { useToasts } from 'react-toast-notifications'
import { TOAST_MESSAGE } from '../../UI/ToastNotification/ToastNotification'
import { defineAvailablePostsActions } from '../../../models/postStatuses'
import {
  getHours12Clock,
  getMinutes12Clock,
  getTimeOfDay12Clock,
  transformEnglishTimeToGeneral24Time
} from '../helpers'
import { SCHEDULE_ACTION_TYPES } from '../../../store/application/types'
import { duplicatePostThunk } from '../../../store/modal/duplicatePost/thunks'
import { setDuplicatedPostAction } from '../../../store/modal/duplicatePost/slice'
import { setSelectedSchedulesAction } from '../../../store/calendar/slice'
import { TPost } from '../../../store/managePost/types'
import { generatePath } from 'react-router-dom'

const MIN_HOURS = 1
const MAX_HOURS = 12

const MIN_MINUTES = 0
const MAX_MINUTES = 59

export enum TIME_OF_DAY {
  AM = 'AM',
  PM = 'PM'
}

export type TTime = {
  hours: string
  minutes: string
  timeOfDay: string
}

export type TSheduleModalData = {
  title: string
  buttonName: string
  isDisabledAutoposting: boolean
  action: () => void
}

// TODO move modal to layout
const SchedulePostModal: FC<any> = () => {
  const {
    scheduleModal,
    checkedDate,
    checkedTime,
    isUpdatingPostSchedule,
    // isOpenMultiPostSchedulesUpdate,
    isAutoposting,
    selectedSchedules,
    project,
    selectedSocials,
    postType,
    location,
    postsForDuplicate,
    scheduleId,
    post,
    date,
    timezone,
    // isPostExist,
    // schedule,
    view
  } = useSelector((state) => ({
    scheduleModal: state.app.scheduleModal,
    postsForDuplicate: state.duplicateModal.postForDuplicate,
    // isOpenMultiPostSchedulesUpdate: state.app.isOpenMultiPostSchedulesUpdate,
    isAutoposting: state.managePost.latestSchedule.is_autoposting,
    checkedDate: state.managePost.latestSchedule.date,
    checkedTime: state.managePost.latestSchedule.time,
    project: state.project.currentProject,
    selectedSchedules: state.calendarApproval.selectedSchedules,
    isUpdatingPostSchedule: state.managePost.loaders.schedules.update,
    selectedSocials: state.managePost.selectedSocials,
    postType: state.managePost.selectedPostType,
    view: state.managePost.viewType,
    scheduleId: state.managePost.latestSchedule.id,
    location: state.router.location,
    post: state.managePost.post,
    date: state.managePost.latestSchedule.date,
    timezone: state.project.currentProject.timezone
    // todo isEditable social
    // isPostExist: state.managePost.isPostExist,
    // schedule: state.managePost.latest_schedule
  }))
  const dispatch = useDispatch()
  const { addToast } = useToasts()
  const [calendarApi, setCalendarApi] = useState<any>()

  const [time, setTime] = useState<TTime>({
    hours: '11',
    minutes: '00',
    timeOfDay: TIME_OF_DAY.PM
  })

  useEffect(() => {
    if (checkedDate && checkedTime) {
      const date = new Date(`${checkedDate}T${checkedTime}`)
      setTime({
        hours: getHours12Clock(date),
        minutes: getMinutes12Clock(date),
        timeOfDay: getTimeOfDay12Clock(date)
      })
    }
  }, [checkedTime, checkedDate])

  const handleTimeChange = ({ value, name }: any) => {
    setTime((prevValue) => ({ ...prevValue, [name]: value }))
  }

  const handleOnBlurTime = ({ value, name }: any) => {
    let result = ''
    if (name === 'minutes') {
      if (+value < MIN_MINUTES) {
        result = '00'
      } else if (+value > MAX_MINUTES) {
        result = `${MAX_MINUTES}`
      } else if (+value < 10) {
        result = `0${Math.floor(+value)}`
      } else {
        result = `${Math.floor(+value)}`
      }
    }

    if (name === 'hours') {
      if (+value < MIN_HOURS) {
        result = '01'
      } else if (+value > MAX_HOURS) {
        result = `${MAX_HOURS}`
      } else if (+value < 10) {
        result = `0${Math.floor(+value)}`
      } else {
        result = `${Math.floor(+value)}`
      }
    }

    setTime((prevValue) => ({ ...prevValue, [name]: result }))
  }

  const allowedShedulesToUpdate = useMemo(() => {
    return selectedSchedules.filter((schedule) =>
      defineAvailablePostsActions.schedule(schedule.post.status)
    )
  }, [selectedSchedules])

  // Chart data
  const [chartData, setChartData] = useState<{
    data: number[]
    labels: string[]
    date: string
  }>({
    data: [],
    labels: [],
    date: ''
  })

  const allowedSocialsForInsights = [
    SOCIAL_NETWORK.FACEBOOK_SOCIAL,
    SOCIAL_NETWORK.INSTAGRAM_SOCIAL
  ]

  useEffect(() => {
    const social = selectedSocials.find((social) => allowedSocialsForInsights.includes(social))

    if (social && checkedDate && project.id) {
      const getChartData = async () => {
        const date = format(parseISO(checkedDate), 'd MMMM yyyy')

        const payload: TPageFansPayload = {
          projectId: project.id,
          socialNetwork: social,
          date: checkedDate
        }

        const {
          payload: {
            data: { values }
          }
        } = await dispatch(getAvailableFansOnlineMetricsBySocialNetwork(payload))
        const dataByNotEven = values.filter((value: number, index: number) => index % 2 !== 0)
        const labels = [
          '1AM',
          '3AM',
          '5AM',
          '7AM',
          '9AM',
          '11AM',
          '1PM',
          '3PM',
          '5PM',
          '7PM',
          '9PM',
          '11PM'
        ]

        setChartData({
          data: dataByNotEven,
          labels,
          date
        })
      }

      getChartData()
    }
  }, [checkedDate, selectedSocials, project])

  const isDatePossiblyForApplying = useMemo(() => {
    const transformedTime = transformEnglishTimeToGeneral24Time(time)
    const queryDate = `${checkedDate}T${transformedTime}`
    const availableDateForPosting = convertToTimeZone(addMinutes(new Date(), 4), {
      timeZone: timezone
    })
    const currentDatetime = convertToTimeZone(new Date(queryDate), { timeZone: timezone })

    return isBefore(availableDateForPosting, currentDatetime)
  }, [time, date])

  // dispatch store
  const setScheduledDate = (date: string) => {
    dispatch(setDateAction(date))
  }

  const close = () => {
    dispatch(closeSchedulePostModal())
  }

  const toggleIsAutoposting = ({ value }: any) => {
    dispatch(toggleIsAutopostingAction())
  }

  const setTimeToStore = useCallback(() => {
    const timeString = transformEnglishTimeToGeneral24Time(time)
    dispatch(setTimeAction(timeString))
  }, [time])

  // Schedule actions
  const setSheduleToRoute = useCallback(() => {
    if (!checkedDate || !selectedSocials.length || !postType) return

    if (project) {
      const stringTime = transformEnglishTimeToGeneral24Time(time)
      const queryDate = `date=${checkedDate}T${stringTime}`
      const querySocials = `&socials=${selectedSocials.join(',')}`
      const queryPostType = `&type=${postType}`
      setTimeToStore()

      close()
      dispatch(
        push(
          `/${project.id}/manage-post/${selectedSocials[0]}?${queryDate}${querySocials}${queryPostType}`
        )
      )
    }
  }, [time, project, checkedDate, selectedSocials, postType, setTimeToStore])

  const updateSingleShedule = async () => {
    dispatch(setIsSchedulingPostAction(true))
    const transformedTime = transformEnglishTimeToGeneral24Time(time)
    const data = await dispatch(
      updateScheduleThunk({
        time: transformedTime,
        schedule_id: scheduleId || allowedShedulesToUpdate[0].id,
        date: checkedDate,
        is_autoposting: !!isAutoposting
      })
    )
    if (isFulfilled(data)) {
      addToast(TOAST_MESSAGE.CHANGE_SAVED, { appearance: 'success' })
    } else if (isRejected(data)) {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
    dispatch(setIsSchedulingPostAction(false))
    close()
  }

  const updateBatchSchedules = async () => {
    const transformedTime = transformEnglishTimeToGeneral24Time(time)
    const payload: TUpdatePostSchedulesPayload = {
      time: transformedTime,
      schedule_ids: allowedShedulesToUpdate.map(({ id }) => id),
      date: checkedDate,
      is_autoposting: isAutoposting
    }
    dispatch(setIsSchedulingPostAction(true))
    const data = await dispatch(updateSchedulesThunk(payload))
    dispatch(setIsSchedulingPostAction(false))
    if (isFulfilled(data)) {
      addToast(TOAST_MESSAGE.CHANGE_SAVED, { appearance: 'success' })
      dispatch(setSelectedSchedulesAction([]))
    } else if (isRejected(data)) {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
    close()
  }

  const scheduleDuplicatedPost = async () => {
    dispatch(setIsSchedulingPostAction(true))
    const transformedTime = transformEnglishTimeToGeneral24Time(time)
    const data = await dispatch(
      duplicatePostThunk({
        ...postsForDuplicate,
        time: transformedTime,
        date: checkedDate,
        is_autoposting: isAutoposting
      })
    )
    dispatch(setIsSchedulingPostAction(false))
    if (isFulfilled(data)) {
      // TODO change it when content page will work with store data. It just for content page after the post was duplicated
      const { id, post_socials: postSocials, project_id: projectId } = data.payload as TPost
      const social: SOCIAL_NETWORK = postSocials[0].social_network

      dispatch(setDuplicatedPostAction(data.payload))
      addToast(TOAST_MESSAGE.CHANGE_SAVED, { appearance: 'success' })
      dispatch(
        push(
          generatePath(AUTH_ROUTE.MANAGE_POST, {
            projectId: projectId,
            social
          }) + `?postId=${id}`
        )
      )
    } else if (isRejected(data)) {
      addToast(TOAST_MESSAGE.GENERAL_ERROR_MASSEGE, { appearance: 'error' })
    }
    close()
  }

  const scheduleBeforeSendToApproval = async () => {
    dispatch(setIsSchedulingPostAction(true))
    const transformedTime = transformEnglishTimeToGeneral24Time(time)
    await dispatch(
      updateScheduleBeforeApprovalThunk({
        time: transformedTime,
        schedule_id: scheduleId || allowedShedulesToUpdate[0].id,
        date: checkedDate,
        is_autoposting: !!isAutoposting
      })
    )
    dispatch(setIsSchedulingPostAction(false))
    close()
  }

  // Conditions to render
  const isContainsSheduled = useMemo(() => {
    switch (scheduleModal.actionType) {
      case SCHEDULE_ACTION_TYPES.BATCH_SCHEDULES: {
        return !!selectedSchedules.find(
          ({ post: { status } }) => status === POST_STATUS.STATUS_SCHEDULED
        )
      }
      default: {
        return post.status === POST_STATUS.STATUS_SCHEDULED
      }
    }
  }, [scheduleModal.actionType, post.status])

  const isContainsInstaStory = useMemo(() => {
    let result
    switch (scheduleModal.actionType) {
      case SCHEDULE_ACTION_TYPES.BATCH_SCHEDULES: {
        result = !!selectedSchedules.find(
          ({ post: { type, post_socials } }) =>
            (type === POST_TYPE.POST_TYPE_PHOTO_STORY ||
              type === POST_TYPE.POST_TYPE_VIDEO_STORY ||
              type === POST_TYPE.POST_TYPE_MEDIA_CAROUSEL) &&
            !!post_socials.find(
              (postSocial: TPostSocialManagePost) =>
                postSocial.social_network === SOCIAL_NETWORK.INSTAGRAM_SOCIAL
            )
        )
        break
      }
      default: {
        result = defineAvailablePostsActions.autoposting(postType, selectedSocials)
      }
    }
    result && dispatch(setIsAutopostingAction(false))
    return result
  }, [scheduleModal.actionType, post.status])

  // Shedule modal data
  const scheduleModalData: TSheduleModalData = useMemo(() => {
    let result
    switch (scheduleModal.actionType) {
      case SCHEDULE_ACTION_TYPES.SHEDULE_TO_ROUTE:
        result = {
          title: 'Schedule Post',
          buttonName: 'Yes',
          action: setSheduleToRoute
        }
        break
      case SCHEDULE_ACTION_TYPES.SINGLE_SCHEDULE:
        result = {
          title: 'Schedule Post',
          buttonName: 'Yes',
          action: updateSingleShedule
        }
        break
      case SCHEDULE_ACTION_TYPES.BATCH_SCHEDULES:
        result = {
          title: 'Update Post Schedules',
          buttonName: 'Update',
          action: updateBatchSchedules
        }
        break
      case SCHEDULE_ACTION_TYPES.SCHEDULE_DUPLICATE:
        result = {
          title: 'Schedule Post',
          buttonName: 'Duplicate',
          action: scheduleDuplicatedPost
        }
        break
      case SCHEDULE_ACTION_TYPES.SCHEDULE_BEFORE_APPROVAL:
        result = {
          title: 'Reschedule post',
          buttonName: 'Update',
          action: scheduleBeforeSendToApproval
        }
        break
      default:
        result = {
          title: 'Schedule Post',
          buttonName: 'Yes',
          action: () => {
            console.log('actionType is = ', scheduleModal.actionType)
          }
        }
    }
    return { ...result, isDisabledAutoposting: isContainsInstaStory }
  }, [
    scheduleModal.actionType,
    isContainsInstaStory,
    isContainsSheduled,
    setSheduleToRoute,
    updateSingleShedule,
    scheduleBeforeSendToApproval,
    updateBatchSchedules,
    scheduleDuplicatedPost
  ])

  const renderScheduleForm = useCallback(
    () => (
      <div className='schedule-calendar__time'>
        <div className='schedule-calendar__time__days-time'>
          <div className='schedule-calendar__time__days-time__item'>
            <RadioInput
              checked={time.timeOfDay === TIME_OF_DAY.AM}
              handleChange={handleTimeChange}
              label={TIME_OF_DAY.AM}
              value={TIME_OF_DAY.AM}
              name='timeOfDay'
            />
          </div>
          <div className='schedule-calendar__time__days-time__item'>
            <RadioInput
              checked={time.timeOfDay === TIME_OF_DAY.PM}
              handleChange={handleTimeChange}
              label={TIME_OF_DAY.PM}
              value={TIME_OF_DAY.PM}
              name='timeOfDay'
            />
          </div>
        </div>
        <div className='schedule-calendar__time__time'>
          <div className='schedule-calendar__time__time__item'>
            <NumberInput
              handleChange={handleTimeChange}
              onBlur={handleOnBlurTime}
              name='hours'
              value={time.hours}
              min={MIN_HOURS}
              max={MAX_HOURS}
            />
          </div>
          :
          <div className='schedule-calendar__time__time__item'>
            <NumberInput
              handleChange={handleTimeChange}
              onBlur={handleOnBlurTime}
              name='minutes'
              value={time.minutes}
              min={MIN_MINUTES}
              max={MAX_MINUTES}
            />
          </div>
        </div>
        <div className='schedule-calendar__is-autoposting'>
          <CheckboxInput
            type='checkbox'
            checked={isAutoposting}
            label='Autoposting'
            disabled={scheduleModalData.isDisabledAutoposting}
            onChange={toggleIsAutoposting}
          />
        </div>
      </div>
    ),
    [
      time,
      isAutoposting,
      scheduleModalData.isDisabledAutoposting,
      handleTimeChange,
      toggleIsAutoposting
    ]
  )

  return (
    <CustomPopup
      open={scheduleModal.isOpen}
      buttonClose={close}
      closeOnDocumentClick={false}
      className='schedule-post'
    >
      <div className='schedule-post__wrapper'>
        <div className='schedule-post__title'>{scheduleModalData.title}</div>
        <div className='schedule-post__content'>
          <div className='schedule-post__content__calendar-wrapper'>
            <div className='schedule-post__content__calendar-wrapper__calendar schedule-calendar'>
              <HeaderToolbar {...{ calendarApi, disableFilters: true, checkedDate }} />
              <FullCalendar
                ref={(calendar) => setCalendarApi(calendar?.getApi())}
                headerToolbar={false}
                dayHeaderFormat={{ weekday: 'long' }}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                dayCellContent={(props) => (
                  <DayCellHover
                    cellProps={props}
                    {...{ checkedDate, setScheduledDate, renderScheduleForm }}
                  />
                )}
                fixedWeekCount={false}
                eventBackgroundColor='#3a98ab'
                events={[{ start: checkedDate, title: 'Selected' }]}
                eventColor='#3a98ab'
                eventBorderColor='#3a98ab'
              />
            </div>
            <div className='schedule-post__content__calendar-wrapper__actions'>
              <CustomButton onClick={close} button='secondary'>
                No
              </CustomButton>
              <CustomButton
                inProgress={isUpdatingPostSchedule}
                disabled={!checkedDate || !isDatePossiblyForApplying}
                onClick={scheduleModalData.action}
                button='primary'
              >
                {scheduleModalData.buttonName}
              </CustomButton>
            </div>
          </div>
          {selectedSocials.some((social) => allowedSocialsForInsights.includes(social)) &&
            chartData && (
              <div className='schedule-post__content__chart'>
                <div className='schedule-post__content__chart__title'>Best time for posting</div>
                <div className='schedule-post__content__chart__tooltip'>{chartData.date}</div>
                <div className='chart'>
                  <BarChart
                    name={'best_time'}
                    social={SOCIAL_NETWORK.FACEBOOK_SOCIAL}
                    chartData={chartData}
                  />
                </div>
              </div>
            )}
        </div>
      </div>
    </CustomPopup>
  )
}

export default SchedulePostModal
